import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/react-dom-interactions'
import { Node as ProseNode } from 'prosemirror-model'
import { type EditorView } from 'prosemirror-view'
import { useLayoutEffect, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'

import { Customize } from 'almanac-ui/icons'
import { ReactComponent as LinkArrow } from 'assets/icons/linkArrow9.svg'
import Avatar from 'common/Avatar/Avatar'
import { CommentInlineFormatButton } from 'common/Prose/components/TextInlineFormatBar/components/Buttons/CommentInlineFormatButton/CommentInlineFormatButton'
import { DeleteInlineFormatButton } from 'common/Prose/components/TextInlineFormatBar/components/Buttons/DeleteInlineFormatButton'
import { ProseDropdownPrompt } from 'common/Prose/components/TextInlineFormatBar/components/Buttons/ProseDropdownPrompt/ProseDropdownPrompt'
import { TodoInlineFormatButton } from 'common/Prose/components/TextInlineFormatBar/components/Buttons/TodoInlineFormatButton/TodoInlineFormatButton'
import { MentionSubject } from 'common/Prose/utils/smartLinks/types'
import { InlineFormatBar } from 'components/InlineFormatBar/InlineFormatBar'
import { InlineFormatButton } from 'components/InlineFormatBar/components/InlineFormatButton/InlineFormatButton'
import { InlineFormatDivider } from 'components/InlineFormatBar/components/InlineFormatDivider/InlineFormatDivider'
import { InlineFormatDropdownMenuItem } from 'components/InlineFormatBar/components/InlineFormatDropdownMenuItem/InlineFormatDropdownMenuItem'
import { useCurrentLayer } from 'hooks/useCurrentLayer'
import { EDITOR_CONTAINER_ID } from 'screens/ProseEdit/Constants'
import { useDocStore } from 'stores/DocStore'
import { getEntityIdFromCurrentUrl } from 'utils/urlStoreUtils'

import { CustomizeCardModal } from './CustomizeCardModal/CustomizeCardModal'

import Styles from './MentionsFormatBar.module.scss'

export const MENTION_FORMAT_BAR_ID = 'mention-format-bar'
const viewTypes = ['inline', 'card', 'frame'] as const

export type viewType = (typeof viewTypes)[number]

type Props = {
  view: EditorView
  close: () => void
  getPos: () => number
  mentionSubject: MentionSubject | null
  subtitle: string
  link: string
  smartLinkUuid?: string
  node: ProseNode
  onLinkClick: (event: React.MouseEvent<any, MouseEvent>) => void
  conversionOptions: {
    [key: string]: { label: string; disabled: boolean; changeFn: () => void; icon: () => JSX.Element }
  }
  parentRef: React.RefObject<HTMLElement>
  forceFormatBarVisible?: (value: boolean) => void
  linkPosition?: { from: number; to: number }
}

export const MentionsFormatBar = ({
  view,
  close,
  getPos,
  mentionSubject,
  subtitle,
  link,
  node,
  onLinkClick,
  conversionOptions,
  parentRef,
  forceFormatBarVisible,
  linkPosition,
  smartLinkUuid,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false)

  const queryClient = useQueryClient()
  const articleId = getEntityIdFromCurrentUrl()
  const layer = useCurrentLayer({ articleKey: articleId ?? '' })
  const docStore = useDocStore()

  const viewType = node?.attrs?.mode || 'inline'

  useLayoutEffect(() => {
    if (containerRef.current && parentRef.current) {
      const shiftBoundary = document.getElementById(EDITOR_CONTAINER_ID)
      const reference = parentRef.current
      const floating = containerRef.current
      autoUpdate(reference, floating, () => {
        computePosition(reference, floating, {
          placement: 'top',
          middleware: [offset(4), shift({ boundary: shiftBoundary ?? document.body }), flip()],
        }).then(({ x, y }) => {
          Object.assign(floating.style, {
            transform: `translate(${x}px, ${y}px)`,
          })
        })
      })
    }
  }, [viewType, containerRef, parentRef])

  if (!mentionSubject) return null
  const { avatar, title } = mentionSubject

  const openCustomizeModal = () => {
    setIsCustomizeModalOpen(true)
  }

  const activeAnchorChildren = () => {
    const { label, icon } = conversionOptions[viewType]
    return (
      <>
        {icon()}
        {label}
      </>
    )
  }

  return (
    <>
      <div
        ref={containerRef}
        id={MENTION_FORMAT_BAR_ID}
        className={Styles.container}
        data-test-id="mentionsFormatBar"
        data-smart-link-uuid={smartLinkUuid}
      >
        <InlineFormatBar close={close}>
          <InlineFormatButton className={Styles.mentionLink} data-testid="mention-link">
            <Link to={link} rel="noopener noreferrer" onClick={onLinkClick}>
              <span className={Styles.avatarWrapper}>
                <Avatar className={Styles.mentionEmoji} size={16} {...avatar} />
                <span className={Styles.avatarLinkArrow}>
                  <LinkArrow />
                </span>
              </span>
              <span className={Styles.mentionTitle}>{title}</span>
            </Link>
          </InlineFormatButton>
          <InlineFormatDivider />
          {node.attrs.type !== 'Handbook' && node.attrs.type !== 'Folder' && (
            <ProseDropdownPrompt
              withChevron
              placement="bottom-start"
              view={view}
              anchorChildren={activeAnchorChildren()}
              label={'Change mention display type'}
              onOpenChange={(open) => forceFormatBarVisible?.(open)}
              dropdownComponent={(close) => (
                <div className={Styles.conversionDropdown}>
                  {Object.keys(conversionOptions).map((key) => {
                    const { label, disabled, changeFn, icon } = conversionOptions[key]
                    const active = key === viewType
                    return (
                      <InlineFormatDropdownMenuItem
                        key={key}
                        isDisabled={disabled}
                        onClick={() => {
                          changeFn()
                          close()
                        }}
                        aria-label={label}
                        label={label}
                        icon={icon()}
                        active={active}
                      />
                    )
                  })}
                </div>
              )}
            />
          )}
          {viewType === 'card' && (
            <InlineFormatButton
              onClick={openCustomizeModal}
              frontIcon={Customize}
              label="Customize"
              data-testid="customize-card"
            />
          )}
          <InlineFormatDivider />
          <CommentInlineFormatButton
            view={view}
            layerId={layer?.id ?? ''}
            queryClient={queryClient}
            docStore={docStore}
            hide={!view.editable && !false}
            nodePos={getPos()}
            onOpenChange={(open) => forceFormatBarVisible?.(open)}
            overrideSelectionRange={linkPosition}
          />
          <TodoInlineFormatButton
            view={view}
            queryClient={queryClient}
            docStore={docStore}
            hide={!view.editable}
            nodePos={getPos()}
            onOpenChange={(open) => forceFormatBarVisible?.(open)}
            overrideSelectionRange={linkPosition}
          />
          <DeleteInlineFormatButton view={view} overrideSelectionRange={linkPosition} />
        </InlineFormatBar>
      </div>
      {viewType === 'card' && (
        <CustomizeCardModal
          isOpen={isCustomizeModalOpen}
          view={view}
          getPos={getPos}
          activeStyle={node.attrs.cardStyle}
          onClose={() => setIsCustomizeModalOpen(false)}
          mentionSubject={mentionSubject}
          mentionType={node.attrs.mentionType}
          subtitle={subtitle}
        />
      )}
    </>
  )
}
